import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Author {
  name: string;
  affiliation: string;
}

interface ResearchCardData {
  id: string;
  title: string;
  authors: Author[];
  journal: string;
  year: number;
  category: string;
  methodology: string;
  abstract: string;
  citations: number;
  pdfUrl: string;
  doi: string;
  keyFindings: string[];
}

interface ResearchCardProps {
  research: ResearchCardData;
  onDownload: (id: string) => void;
  onCite: (id: string) => void;
}

const ResearchCard: React.FC<ResearchCardProps> = ({ research, onDownload, onCite }) => {
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      forgiveness: 'bg-accent/20 text-accent-foreground',
      peace: 'bg-primary/20 text-primary',
      conflict: 'bg-secondary/20 text-secondary',
      trauma: 'bg-error/20 text-error',
      community: 'bg-success/20 text-success',
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-border overflow-hidden">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-body font-medium ${getCategoryColor(
                  research.category
                )}`}
              >
                {research.category.charAt(0).toUpperCase() + research.category.slice(1)}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-body font-medium bg-muted text-muted-foreground">
                {research.methodology}
              </span>
            </div>
            <h3 className="text-xl font-headline font-bold text-primary mb-2 leading-tight">
              {research.title}
            </h3>
          </div>
        </div>

        {/* Authors */}
        <div className="mb-4">
          <p className="text-sm font-body text-foreground">
            {research.authors.map((author, index) => (
              <span key={index}>
                {author.name}
                {index < research.authors.length - 1 && ', '}
              </span>
            ))}
          </p>
          <p className="text-xs font-body text-muted-foreground mt-1">
            {research.journal} • {research.year}
          </p>
        </div>

        {/* Abstract */}
        <p className="text-sm font-body text-foreground/80 mb-4 line-clamp-3">
          {research.abstract}
        </p>

        {/* Key Findings */}
        {research.keyFindings.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-body font-semibold text-foreground mb-2">
              Key Findings:
            </h4>
            <ul className="space-y-1">
              {research.keyFindings.slice(0, 2).map((finding, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <Icon
                    name="CheckCircleIcon"
                    size={16}
                    className="text-success flex-shrink-0 mt-0.5"
                  />
                  <span className="text-xs font-body text-foreground/80">{finding}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1 text-muted-foreground">
              <Icon name="DocumentTextIcon" size={16} />
              <span className="text-xs font-body">{research.citations} citations</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onCite(research.id)}
              className="px-3 py-1.5 border border-primary text-primary rounded-md font-body text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
            >
              Cite
            </button>
            <button
              onClick={() => onDownload(research.id)}
              className="px-3 py-1.5 bg-primary text-primary-foreground rounded-md font-body text-xs font-medium hover:bg-primary/90 transition-colors duration-200 flex items-center space-x-1"
            >
              <Icon name="ArrowDownTrayIcon" size={14} />
              <span>Download PDF</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchCard;